import React, { memo, useCallback } from 'react';
import clsx from 'clsx';
import { Box } from '@alf/uikit';
import { LOG_ACTIONS, logAction } from '@namespace/analytics';
import { useOtherSettings } from '@namespace/cms';
import { useDeviceStore } from '@namespace/device';
import {
  DEFAULT_ROWS_DESKTOP_TABLET,
  DEFAULT_ROWS_MOBILE,
  ROWS_WITH_GAMES
} from '../../constants';
import { useScrollToPrevGamesPosition } from '../../hooks';
import { OnGameClick } from '../../types';
import GamesList from '../GamesList';
import GamesSectionHeader from '../GamesSectionHeader';
import { GamesSectionProps } from './types';

import styles from './index.module.css';

const getDefaultRowsKindByDevice = (
  isMobile: boolean,
  isFetching?: boolean
) => {
  if (isFetching) {
    return ROWS_WITH_GAMES.ALL;
  }

  return isMobile ? DEFAULT_ROWS_MOBILE : DEFAULT_ROWS_DESKTOP_TABLET;
};

const GamesSection: React.FC<GamesSectionProps> = memo(
  ({
    games = [],
    wideGames = [],
    title = '',
    viewAll = '',
    firstGamePreview = false,
    kind,
    classNames = {},
    categoryName = '',
    config,
    sectionName,
    isFetching,
    isPaginationEnabled = false,
    onViewAllClick,
    page,
    handlePageChange,
    ...rest
  }) => {
    const { casinoType, showInstaGamesView } = config ?? {};
    const { isMobile, isDesktop }: { isMobile: boolean; isDesktop: boolean } =
      useDeviceStore();
    const { isGameCardsMinimisedConfig = false } = useOtherSettings();
    const kindByDevice = getDefaultRowsKindByDevice(isMobile, isFetching);
    const {
      sectionClassName = '',
      gamesHeaderClassName,
      searchGameSectionTitle = '',
      searchGameSectionTitleWrapper = '',
      ...restClasses
    } = classNames;

    const onGameClick: OnGameClick = useCallback(
      (data) => {
        logAction(LOG_ACTIONS.OPEN_CASINO_GAME, {
          ...data,
          casinoCategory: categoryName || `All games || ${sectionName}`
        });
      },
      [categoryName, sectionName]
    );

    const gamesListId = categoryName ? `gamesList-${categoryName}` : '';

    useScrollToPrevGamesPosition(gamesListId, isPaginationEnabled, casinoType);

    return (
      <Box
        direction="column"
        className={clsx([
          styles.container,
          sectionClassName,
          isGameCardsMinimisedConfig &&
            !isDesktop &&
            styles.containerMobileGameCardsMinimised
        ])}
      >
        {title && (
          <GamesSectionHeader
            title={title}
            viewAll={viewAll}
            onViewAllClick={onViewAllClick}
            className={gamesHeaderClassName}
            classNames={{
              searchGameSectionTitle,
              searchGameSectionTitleWrapper
            }}
          />
        )}
        <GamesList
          config={config}
          games={games}
          wideGames={wideGames}
          firstGamePreview={firstGamePreview}
          kind={kind || kindByDevice}
          classNames={restClasses}
          onGameClick={onGameClick}
          showInstaGamesView={showInstaGamesView}
          key={gamesListId}
          sectionName={sectionName}
          categoryName={categoryName}
          {...(isPaginationEnabled
            ? {
                page,
                onChangePage: handlePageChange,
                id: gamesListId
              }
            : {})}
          {...rest}
        />
      </Box>
    );
  }
);

export default GamesSection;
